import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Send, FileText, Loader2, Check, X, Search, Pencil, Coins, Trash2, Share2, Copy } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import QRCode from 'qrcode';
import { IcpcSearchBar } from './IcpcSearchBar';
import { stelIcpcVoor } from '@/lib/ai';
import { VerlatenWaarschuwing } from '@/components/VerlatenWaarschuwing';
import { api } from '@/lib/api';
import type { DeellinkAntwoord } from '@/lib/api-types';
import { PracticeSettings } from '@/types/practice-settings';

export interface SoepProblem {
  title: string | null;
  subjective: string | null;
  objective: string | null;
  evaluation: string | null;
  plan: string | null;
  icpc_code?: string | null;
  icpc_description?: string | null;
}

export interface SoepPayload {
  consultation_date: string;
  problems: SoepProblem[];
  subjective: string | null;
  objective: string | null;
  evaluation: string | null;
  plan: string | null;
  transcript: string | null;
}

interface IcpcSuggestion {
  code: string;
  description: string;
}

interface SoepReviewScreenProps {
  payload: SoepPayload;
  onSend: (payload: SoepPayload) => void;
  onCancel: () => void;
  onDelete?: () => void;
  autoSuggestIcpc: boolean;

  practiceSettings: PracticeSettings;
  practiceName?: string;
  consultationCosts?: {
    recording: number;
    translation: number;
    summarization: number;
    icpc: number;
    total: number;
  };
}

export function SoepReviewScreen({ payload, onSend, onCancel, onDelete, autoSuggestIcpc, practiceSettings, practiceName, consultationCosts }: SoepReviewScreenProps) {
  const [problems, setProblems] = useState<SoepProblem[]>(payload.problems);
  const [suggestions, setSuggestions] = useState<Record<number, IcpcSuggestion[]>>({});
  const [loading, setLoading] = useState<Record<number, boolean>>({});
  const [suggestionErrors, setSuggestionErrors] = useState<Record<number, string>>({});
  const [editingField, setEditingField] = useState<{ index: number; field: string } | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareQrDataUrl, setShareQrDataUrl] = useState<string | null>(null);
  const [shareExpiresAt, setShareExpiresAt] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);

  const updateField = (problemIndex: number, field: keyof SoepProblem, value: string) => {
    setProblems(prev => prev.map((p, i) =>
      i === problemIndex ? { ...p, [field]: value || null } : p
    ));
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const idx = deleteIndex;
    setDeleteIndex(null);
    if (problems.length <= 1) {
      if (onDelete) onDelete();
      else onCancel();
      return;
    }
    setProblems(prev => prev.filter((_, i) => i !== idx));
    setSuggestions(prev => {
      const next: Record<number, IcpcSuggestion[]> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = Number(k);
        if (n < idx) next[n] = v;
        else if (n > idx) next[n - 1] = v;
      });
      return next;
    });
    toast({ title: 'Deelcontact verwijderd' });
  };

  // Auto-fetch ICPC suggestions for each problem's evaluation
  useEffect(() => {
    if (!autoSuggestIcpc) return;
    problems.forEach((problem, index) => {
      if (problem.evaluation && problem.evaluation.trim().length >= 3) {
        fetchSuggestions(index, problem.evaluation);
      }
    });
  }, []); // Only on mount

  const fetchSuggestions = async (index: number, evaluationText: string) => {
    setLoading(prev => ({ ...prev, [index]: true }));
    setSuggestionErrors(prev => ({ ...prev, [index]: '' }));
    try {
      // De server valideert de codes tegen de codelijst, dus wat hier uitkomt bestaat.
      // De sleutel en de prompt zijn niet meer nodig: die staan server-side.
      const { suggesties: results } = await stelIcpcVoor(evaluationText);
      setSuggestions(prev => ({ ...prev, [index]: results }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout bij ophalen ICPC suggesties';
      console.error('Failed to fetch ICPC suggestions:', message);
      setSuggestionErrors(prev => ({ ...prev, [index]: message }));
      toast({ title: 'ICPC suggestie mislukt', description: message, variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const selectIcpc = (problemIndex: number, suggestion: IcpcSuggestion) => {
    setProblems(prev => prev.map((p, i) => 
      i === problemIndex 
        ? { ...p, icpc_code: p.icpc_code === suggestion.code ? null : suggestion.code, icpc_description: p.icpc_code === suggestion.code ? null : suggestion.description }
        : p
    ));
  };

  // Vanuit de zoekbalk: altijd de gekozen code instellen (nooit toggelen).
  const setIcpcFromSearch = (problemIndex: number, code: string, description: string) => {
    setProblems(prev => prev.map((p, i) =>
      i === problemIndex
        ? { ...p, icpc_code: code, icpc_description: description }
        : p
    ));
  };

  const handleSend = () => {
    const updatedPayload: SoepPayload = {
      ...payload,
      problems,
      // Top-level fields from first problem for backward compatibility
      subjective: problems[0]?.subjective || null,
      objective: problems[0]?.objective || null,
      evaluation: problems[0]?.evaluation || null,
      plan: problems[0]?.plan || null,
    };
    console.log('[SoepReview] Verstuur payload', updatedPayload.problems.map(p => ({
      title: p.title, icpc_code: p.icpc_code, icpc_description: p.icpc_description,
    })));
    onSend(updatedPayload);
  };

  const isMulti = problems.length > 1;

  const openShare = async () => {
    setShareOpen(true);
    if (shareUrl) return; // al aangemaakt voor dit consult
    setShareLoading(true);
    setShareError(null);
    try {
      // De praktijknaam gaat niet meer mee: die haalt de server uit de sessie. In de
      // bron kwam hij uit de request, dus stond er op de patiëntpagina de naam die
      // de client meestuurde.
      const antwoord = await api.post<DeellinkAntwoord>('/patient-share', {
        soepPayload: { ...payload, problems },
      });

      const link = `${window.location.origin}/patient-share/${antwoord.token}`;
      setShareUrl(link);
      setShareExpiresAt(antwoord.expiresAt);
      const qr = await QRCode.toDataURL(link, { width: 320, margin: 1, errorCorrectionLevel: 'M' });
      setShareQrDataUrl(qr);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Aanmaken mislukt');
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link gekopieerd' });
    } catch {
      toast({ title: 'Kopiëren mislukt', variant: 'destructive' });
    }
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      {/*
        Zolang dit scherm openstaat is de SOEP nog niet naar het EPD gegaan, en het
        transcript waaruit hij komt bestaat niet meer. Onvoorwaardelijk dus: het
        scherm bestaat alleen bij een concept dat nog verzonden of weggegooid moet
        worden.
      */}
      <VerlatenWaarschuwing
        actief
        titel="Het SOEP-verslag is nog niet verzonden"
        beschrijving="Als u dit scherm verlaat gaat het verslag verloren; het is nog niet naar het EPD gestuurd en het transcript is er niet meer om het opnieuw te maken."
      />

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-3 space-y-2 max-w-xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">
              {isMulti ? `${problems.length} deelcontacten` : 'SOEP verslag'}
            </span>
          </div>
          {isMulti && (
            <span className="text-xs text-muted-foreground">worden aangemaakt in het HIS</span>
          )}
        </div>

        {/* Problem cards */}
        <div className="space-y-2">
          {problems.map((problem, index) => (
            <div key={index} className="rounded-md border border-border bg-card">
              {/* Problem header */}
              <div className="flex items-center gap-1.5 px-2.5 pt-2 pb-1">
                {isMulti && (
                  <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0">
                    {index + 1}
                  </span>
                )}
                <span className="text-xs font-semibold text-foreground truncate">
                  {problem.title || (isMulti ? `Deelcontact ${index + 1}` : 'Consult')}
                </span>
                {problem.icpc_code && (
                  <Badge
                    variant="secondary"
                    className="ml-auto text-[10px] h-4 px-1.5 gap-1 bg-primary/10 text-primary border border-primary/30 max-w-[60%]"
                    title={problem.icpc_description || undefined}
                  >
                    <Check className="h-2.5 w-2.5 shrink-0" />
                    <span className="font-bold shrink-0">{problem.icpc_code}</span>
                    {problem.icpc_description && (
                      <span className="truncate font-normal">{problem.icpc_description}</span>
                    )}
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={() => setDeleteIndex(index)}
                  className={`${problem.icpc_code ? '' : 'ml-auto'} inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0`}
                  title={isMulti ? 'Deelcontact verwijderen' : 'SOEP verwijderen'}
                  aria-label={isMulti ? 'Deelcontact verwijderen' : 'SOEP verwijderen'}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>

              {/* SOEP fields */}
              <div className="px-2.5 pb-2">
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                  {(['subjective', 'objective', 'evaluation', 'plan'] as const).map((field) => {
                    const label = field[0].toUpperCase();
                    const value = problem[field];
                    if (!value && !(editingField?.index === index && editingField?.field === field)) return null;
                    const isEditing = editingField?.index === index && editingField?.field === field;
                    return (
                      <React.Fragment key={field}>
                        <span className="font-bold text-muted-foreground pt-0.5">{label}</span>
                        {isEditing ? (
                          <textarea
                            autoFocus
                            value={value || ''}
                            onChange={(e) => updateField(index, field, e.target.value)}
                            onBlur={() => setEditingField(null)}
                            className="w-full text-[11px] text-foreground bg-background border border-input rounded px-1.5 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-ring min-h-[2.5rem]"
                            rows={2}
                          />
                        ) : (
                          <span
                            className="text-foreground cursor-pointer hover:bg-muted/50 rounded px-0.5 -mx-0.5 group flex items-start gap-1"
                            onClick={() => setEditingField({ index, field })}
                          >
                            <span className="line-clamp-3 flex-1">{value}</span>
                            <Pencil className="h-2.5 w-2.5 shrink-0 mt-0.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* ICPC suggestions row */}
                {problem.evaluation && (
                  <div className="mt-1.5 pt-1.5 border-t border-border/50">
                    <div className="flex items-center gap-1 mb-1">
                      <Search className="h-2.5 w-2.5 text-muted-foreground" />
                      <span className="text-[10px] text-muted-foreground font-medium">ICPC</span>
                      {loading[index] && <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground" />}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(suggestions[index] || []).map((s) => {
                        const isSelected = problem.icpc_code === s.code;
                        return (
                          <button
                            key={s.code}
                            type="button"
                            onClick={() => selectIcpc(index, s)}
                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] border transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-muted/50 text-foreground border-border hover:bg-muted hover:border-primary/50'
                            }`}
                          >
                            <span className="font-bold">{s.code}</span>
                            <span className="truncate max-w-[120px]">{s.description}</span>
                            {isSelected && <Check className="h-2.5 w-2.5 shrink-0" />}
                          </button>
                        );
                      })}
                      {!loading[index] && suggestionErrors[index] && (
                        <span className="text-[10px] text-destructive italic">{suggestionErrors[index]}</span>
                      )}
                      {!loading[index] && !suggestionErrors[index] && (!suggestions[index] || suggestions[index].length === 0) && (
                        <span className="text-[10px] text-muted-foreground italic">Geen suggesties</span>
                      )}
                    </div>
                    <IcpcSearchBar
                      selectedCode={problem.icpc_code}
                      onSelect={(code, description) => setIcpcFromSearch(index, code, description)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky bottom actions */}
      <div className="shrink-0 border-t border-border bg-background p-2.5 max-w-xl mx-auto w-full space-y-2">
        {consultationCosts && consultationCosts.total > 0 && (
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground px-1">
            <Coins className="h-3 w-3 shrink-0" />
            <span className="font-medium">Consult kosten:</span>
            {consultationCosts.recording > 0 && <span>Opname €{consultationCosts.recording.toFixed(2)}</span>}
            {consultationCosts.translation > 0 && <span>· Vertaling €{consultationCosts.translation.toFixed(2)}</span>}
            {consultationCosts.summarization > 0 && <span>· SOEP €{consultationCosts.summarization.toFixed(2)}</span>}
            {consultationCosts.icpc > 0 && <span>· ICPC €{consultationCosts.icpc.toFixed(2)}</span>}
            <span className="font-semibold text-foreground ml-auto">Totaal €{consultationCosts.total.toFixed(2)}</span>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>
            Annuleren
          </Button>
          {practiceSettings.patient_share_enabled && (
            <Button variant="secondary" size="sm" className="flex-1" onClick={openShare}>
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Deel met patiënt
            </Button>
          )}
          <Button size="sm" className="flex-1" onClick={handleSend}>
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Verstuur{isMulti ? ` (${problems.length})` : ''}
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteIndex !== null} onOpenChange={(open) => { if (!open) setDeleteIndex(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {problems.length <= 1 ? 'SOEP verwijderen?' : 'Deelcontact verwijderen?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {problems.length <= 1
                ? 'Deze SOEP is nog niet verzonden naar het HIS. Weet je zeker dat je het concept definitief wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.'
                : 'Weet je zeker dat je dit deelcontact wilt verwijderen? De overige deelcontacten blijven behouden. Deze actie kan niet ongedaan worden gemaakt.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Behouden</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>QR-code voor de patiënt</DialogTitle>
            <DialogDescription>
              Laat de patiënt deze QR-code scannen met de mobiele telefoon. De patiënt kiest een taal en kan dan een PDF-samenvatting van het consult downloaden.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3">
            {shareLoading && (
              <div className="h-[320px] w-[320px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {shareError && (
              <div className="text-sm text-destructive text-center">{shareError}</div>
            )}
            {shareQrDataUrl && (
              <img src={shareQrDataUrl} alt="QR-code voor patiëntsamenvatting" className="h-[260px] w-[260px] rounded-md border border-border" />
            )}
            {shareUrl && (
              <div className="w-full space-y-2">
                <div className="flex items-center gap-1.5">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 text-[11px] px-2 py-1.5 rounded border border-input bg-muted/30 font-mono truncate"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={copyShareUrl}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  Eenmalig bruikbaar · vervalt {shareExpiresAt ? new Date(shareExpiresAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : 'binnen 24 uur'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
